import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import { Fab, type SxProps, type Theme } from '@mui/material';
import type { AssistantAction } from '@prospero/shared/src/assistant/assistant-contract';
import { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { AssistantPanel, type AssistantPanelProps } from './assistant-panel';

/** Default CTA handler: jump to the guide topic the assistant pointed at. */
export function useGuideHitNavigation() {
  const navigate = useNavigate();
  return useCallback(
    (action: AssistantAction) => {
      if (action.kind === 'OPEN_GUIDE_HIT') {
        navigate(action.url, { state: { guideHighlight: action.highlight } });
      }
    },
    [navigate],
  );
}

type PanelPassthroughProps = Omit<
  AssistantPanelProps,
  'entryButtonRef' | 'onClose' | 'onOpenGuideHit' | 'open' | 'pageUrl'
>;

interface AssistantShellFrameProps extends PanelPassthroughProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Overrides the guide-hit navigation (hosts with extra CTA kinds). */
  onOpenGuideHit?: (action: AssistantAction) => void;
  /** Stacking of the entry button on pages that render above the app shell. */
  fabSx?: SxProps<Theme>;
}

/**
 * Floating entry button + assistant panel shared by every route shell. Hosts
 * keep what is theirs (layout, gating, panel hooks) and own the open state;
 * the frame only wires the button, the panel and the guide-link navigation.
 */
export function AssistantShellFrame({
  open,
  onOpenChange,
  onOpenGuideHit,
  fabSx,
  ...panelProps
}: AssistantShellFrameProps) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const entryButtonRef = useRef<HTMLButtonElement>(null);
  const navigateToGuideHit = useGuideHitNavigation();

  return (
    <>
      {!open ? (
        <Fab
          aria-label={t('guida.assistant.entry')}
          color="primary"
          onClick={() => onOpenChange(true)}
          ref={entryButtonRef}
          sx={[
            {
              position: 'fixed',
              bottom: { xs: 'calc(72px + env(safe-area-inset-bottom, 0px))', md: 24 },
              right: { xs: 16, md: 24 },
            },
            ...(Array.isArray(fabSx) ? fabSx : fabSx ? [fabSx] : []),
          ]}
        >
          <AutoAwesomeOutlinedIcon />
        </Fab>
      ) : null}
      <AssistantPanel
        {...panelProps}
        entryButtonRef={entryButtonRef}
        onClose={() => onOpenChange(false)}
        onOpenGuideHit={onOpenGuideHit ?? navigateToGuideHit}
        open={open}
        pageUrl={pathname}
      />
    </>
  );
}
